import React from 'react';
import { action, computed, makeObservable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase, InjectedStyledComponent } from '../../../../../shared/index.js';
import { Checkbox } from '../../../Checkbox/Checkbox.js';
import { ITableColumn } from '../../../Table/models/ITableColumn.js';
import { ITableRow } from '../../../Table/models/ITableRow.js';
import ToggleButton from '../../../ToggleButton/ToggleButton.js';
import { ITableGrouping } from '../../models/ITableGrouping.js';
import { ITableGroupingToggle } from '../../models/ITableGroupingToggle.js';
import { Position } from '../../models/Position.js';
import { ToggleState } from '../../models/ToggleState.js';

import { IGroupHeaderConfiguration, IGroupHeaderProps, IGroupHeaderState } from './models/index.js';
import ToggleButtonWrapper from './styledComponents/ToggleButtonWrapper.js';
import Value from './styledComponents/Value.js';
import {
	GROUP_HEADER_CATEGORY_KEY,
	GROUP_HEADER_COMPONENT_KEY,
	GROUP_HEADER_PARENT_COMPONENT_KEY,
} from './GroupHeader.meta.js';

export class GroupHeader extends ComponentBase<IGroupHeaderProps, IGroupHeaderState, IGroupHeaderConfiguration> {
	categoryKey = GROUP_HEADER_CATEGORY_KEY;
	componentKey = GROUP_HEADER_COMPONENT_KEY;
	get parentComponentKey(): string | undefined {
		return GROUP_HEADER_PARENT_COMPONENT_KEY;
	}

	constructor(props: IGroupHeaderProps) {
		super(props);

		makeObservable(this, {
			grouping: computed,
			toggle: computed,
			toggleEnabled: computed,
			togglePosition: computed,
			allowSelection: computed,
			selectionColumn: computed,
			handleSelectChange: action,
		});
	}

	handleToggle = async (value: boolean) => {
		const { onToggle } = this.props;
		if (onToggle) {
			onToggle(value ? ToggleState.expanded : ToggleState.collapsed);
		}
	};

	get grouping(): ITableGrouping {
		return this.props.grouping;
	}

	get toggle(): ITableGroupingToggle | undefined {
		return this.grouping.toggle;
	}

	get toggleEnabled(): boolean {
		return !!(this.toggle && this.toggle.enabled);
	}

	get togglePosition(): Position {
		return (this.toggle && this.toggle.position) || Position.left;
	}

	get allowSelection(): boolean {
		return !!this.selectionColumn;
	}

	get selectionColumn(): ITableColumn | undefined {
		const columns = (this.grouping.tableProps && this.grouping.tableProps.columns) || [];
		return columns.find((column) => column.key === '_selected');
	}

	get rows(): ITableRow[] {
		const { rows = [] } = this.props;
		return rows;
	}

	handleSelectChange = (value: boolean) => {
		if (this.selectionColumn && this.selectionColumn.checkboxProps) {
			const { onChange } = this.selectionColumn.checkboxProps;
			if (onChange) {
				this.rows.forEach((row) => {
					onChange(row, value);
				});
				if (this.props.onSelectionChange) {
					this.props.onSelectionChange(value);
				}
			}
		}
	};

	render() {
		const {
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			children,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			grouping,
			value,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			onToggle,
			toggleState,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			rows,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			onSelectionChange,
			selected,
			analyticsContext,
			suppressAnalytics,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			configuration,
			...otherProps
		} = this.props;

		const toggleButton = (
			<ToggleButtonWrapper>
				<ToggleButton
					active={toggleState === ToggleState.expanded}
					onChange={this.handleToggle}
					suppressAnalytics={suppressAnalytics}
					analyticsContext={analyticsContext}
				/>
			</ToggleButtonWrapper>
		);

		return (
			<div {...otherProps}>
				{this.toggleEnabled && this.togglePosition === Position.left && toggleButton}
				{this.allowSelection && (
					<Checkbox value={selected} onChange={(event) => this.handleSelectChange(event.target.checked)} />
				)}
				<Value>{value}</Value>
				{this.toggleEnabled && this.togglePosition === Position.right && toggleButton}
			</div>
		);
	}
}

export default InjectedStyledComponent(
	observer(GroupHeader),
	GROUP_HEADER_CATEGORY_KEY,
	GROUP_HEADER_PARENT_COMPONENT_KEY,
	[GROUP_HEADER_COMPONENT_KEY],
);
